// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { FORMID, TestRunParams } from "components/TestRun/TestRunConstants";
import {
  FedExDropLocation,
  FedExError,
  FedExPickUpAddress,
  FedExShipperDetails,
  ProposedFedExPickupTime,
} from "./FedExTypes";
import {
  Question,
  QuestionValidator,
  SelectOption,
} from "components/ui/Questionnaire/QuestionnaireTypes";
import React, { useCallback, useEffect, useState } from "react";
import { createStyles, makeStyles } from "@material-ui/core";
import {
  differenceInHours,
  isToday,
  isTomorrow,
  parse,
  parse as parseDate,
} from "date-fns";

import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";
import Button from "@material-ui/core/Button";
import Divider from "components/ui/Divider";
import { FEDEX_PRODUCTION_TOKEN } from "./FedExConstants";
import LoadingOverlay from "components/ui/LoadingOverlay";
import NextStepButton from "components/TestRun/NextStepButton";
import Paragraph from "components/ui/Paragraph";
import Questionnaire from "components/ui/Questionnaire/Questionnaire";
import QuestionnaireInput from "components/ui/Questionnaire/QuestionnaireInput";
import Title from "components/ui/Title";
import { getEnvironmentSync } from "utils/useEnvironment";
import { getFirebaseAppSync } from "components/Firebase/Firebase";
import { setStepReady } from "components/TestRun/TestRunStepReadyEmitter";
import useFedExFetchApi from "./useFedExFetchApi";
import { useParams } from "react-router-dom";
import { useTestRunConfig } from "components/TestRun/useTestRunConfig";
import { useTestRunState } from "store/testrun";

const useStyle = makeStyles(theme =>
  createStyles({
    button: {
      fontSize: "14px",
      whiteSpace: "break-spaces",
    },
    trackingNumber: {
      maxWidth: "300px",
    },
    dashedDivider: {
      borderWidth: "3px",
      borderTopStyle: "dashed",
      marginBottom: "34px",
    },
    warning: {
      color: "red",
      fontSize: "50px",
    },
  })
);

const PICKUP_WINDOWS_CUTOFF_HOURS = 12;

interface FedExSelectTimeProps {
  proposedTimes: Array<ProposedFedExPickupTime>;
  shipperDetails: FedExShipperDetails;
  address: FedExPickUpAddress;
  onEditAddress: (error?: FedExError) => void;
  onPickupScheduled: (
    window: ProposedFedExPickupTime,
    packageLocation: FedExDropLocation,
    remarks: string,
    confirmation: string
  ) => void;
}

export default (props: FedExSelectTimeProps) => {
  const { step } = useParams<TestRunParams>();
  const [fetchState, { scheduleFedexPickup }] = useFedExFetchApi();
  const {
    address,
    proposedTimes,
    shipperDetails,
    onPickupScheduled,
    onEditAddress,
  } = props;
  const { getNextDefaultStep } = useTestRunConfig();
  const classes = useStyle();
  const [pickupWindow, setPickupWindow] = useState<ProposedFedExPickupTime>();
  const [packageLocation, setPackageLocation] = useState<FedExDropLocation>();
  const [remarks, setRemarks] = useState<string>();
  const [windowCutOff, setWindowCutOff] = useState(PICKUP_WINDOWS_CUTOFF_HOURS);
  const firebaseApp = getFirebaseAppSync();
  const testRun = useTestRunState().testRun;

  const stepReadyCallback = useCallback(
    (stepReady: boolean) => {
      setStepReady(step, stepReady);
    },
    [step]
  );

  let willPostFedExToProd = false;
  if (testRun?.registrationToken) {
    willPostFedExToProd =
      testRun?.registrationToken.indexOf(FEDEX_PRODUCTION_TOKEN) > -1;
  }

  const environment = getEnvironmentSync();

  const showWarning =
    willPostFedExToProd &&
    (!environment.isProductionFrontend || // always show if not on prod front-end.
      (environment.isProductionFrontend &&
        firebaseApp.remoteConfig.getBoolean("showFedExAPIWarningInProd")));

  useEffect(() => {
    if (fetchState.isLoading) {
      setStepReady(step, false);
    } else if (fetchState.isError) {
      onEditAddress(FedExError.SCHEDULE_PICKUP_FAILED);
    } else if (fetchState.data) {
      setStepReady(step, true);
      onPickupScheduled(
        pickupWindow!,
        packageLocation!,
        remarks!,
        fetchState.data.result.locationCode +
          fetchState.data.result.confirmationNumber
      );
    }
  }, [
    fetchState.data,
    fetchState.isError,
    fetchState.isLoading,
    getNextDefaultStep,
    step,
    onPickupScheduled,
    onEditAddress,
    pickupWindow,
    packageLocation,
    remarks,
  ]);

  const days: string[] = Array.from(
    props.proposedTimes.reduce(
      (days: Set<string>, time) => days.add(time.date),
      new Set<string>()
    )
  ).sort();

  const registrationToken = testRun?.registrationToken;
  const onSubmit = useCallback(
    (event: React.FormEvent, questions: { [key: string]: Question }) => {
      event.preventDefault();

      const selectedIndex = parseInt(questions["datetime" + days[0]].value!);
      const { humanDate, humanTime, ...selectedTime } = proposedTimes[
        selectedIndex
      ];
      const location =
        (questions.packagelocation.value as FedExDropLocation) || "NONE";
      setPickupWindow(proposedTimes[selectedIndex]);
      setPackageLocation(location);
      setRemarks(questions.remarks.value);

      scheduleFedexPickup({
        ...shipperDetails,
        ...selectedTime,
        address,
        packageLocation: location,
        remarks: questions.remarks.value || "",
        registrationToken: registrationToken!,
      });
    },
    [
      proposedTimes,
      scheduleFedexPickup,
      shipperDetails,
      days,
      address,
      registrationToken,
    ]
  );

  const pickupTimeValidator: QuestionValidator = useCallback(
    (text, questions): string | undefined => {
      // Set the same value on all the datetime questions. The questions that
      // don't contain this value as one of the options won't highlight any of
      // their pills.
      days.forEach(day => {
        const question = questions["datetime" + day];
        if (question) {
          question.value = text || undefined;
        }
      });
      return;
    },
    [days]
  );

  const visibleProposedTimes = filterPickupWindows(proposedTimes, windowCutOff);
  const incrementWindowCutOff = () => {
    let newCutOff = windowCutOff;
    do {
      newCutOff += PICKUP_WINDOWS_CUTOFF_HOURS;
    } while (
      filterPickupWindows(proposedTimes, newCutOff).length ===
      visibleProposedTimes.length
    );
    setWindowCutOff(newCutOff);
  };
  if (visibleProposedTimes.length === 0 && props.proposedTimes.length !== 0) {
    incrementWindowCutOff();
    return null;
  }

  return (
    <>
      <Title>Select a pickup window</Title>
      <Paragraph>
        Select the earliest pickup window below to ensure the specimen is
        returned to the lab as quickly as possible.
      </Paragraph>
      <Questionnaire
        formId={FORMID}
        onSubmit={onSubmit}
        onFormReady={stepReadyCallback}
      >
        {days.map((date, dateIndex) => {
          const options = visibleProposedTimes
            .map((time, idx): SelectOption & {
              date: string;
              humanDate: string;
            } => ({
              value: idx.toString(),
              name: time.humanTime,
              date: time.date,
              humanDate: time.humanDate,
              grid: {
                xs: 6,
                sm: 3,
              },
            }))
            .filter(time => time.date === date);
          if (options.length === 0) {
            return null;
          }
          const parsedDate = parse(options[0].date, "yyyy-MM-dd", new Date());
          const dateLabel = isToday(parsedDate)
            ? "(today)"
            : isTomorrow(parsedDate)
            ? "(tomorrow)"
            : undefined;
          return (
            <QuestionnaireInput
              type="radiobutton"
              label={
                dateLabel
                  ? options[0].humanDate + ` ${dateLabel}`
                  : options[0].humanDate
              }
              validators={[pickupTimeValidator]}
              mandatory={dateIndex === 0}
              name={"datetime" + date}
              selectOptions={options}
              classNames={{ button: classes.button }}
              gridLayout={true}
            />
          );
        })}
        {visibleProposedTimes.length < proposedTimes.length && (
          <Button color="primary" onClick={incrementWindowCutOff}>
            Show More
          </Button>
        )}
        <Divider className={classes.dashedDivider} />
        <QuestionnaireInput
          type="radiobutton"
          label="Pickup location (optional)"
          placeholder="Package location"
          name="packagelocation"
          mandatory={false}
          value={"NONE"}
          selectOptions={[
            { value: "FRONT", name: "Front" },
            { value: "REAR", name: "Rear" },
            { value: "SIDE", name: "Side" },
          ]}
          classNames={{ button: classes.button }}
        />
        <QuestionnaireInput
          label="Additional pickup instructions:"
          mandatory={false}
          name="remarks"
          placeholder="Write instructions here"
          type="text"
        />
      </Questionnaire>
      {showWarning && (
        <Alert severity="error">
          <AlertTitle>THIS IS NOT A TEST</AlertTitle>
          <div>A REAL FEDEX TRUCK WILL SHOW UP!</div>
        </Alert>
      )}
      {!willPostFedExToProd && (
        <Alert severity="success">
          <AlertTitle>OK TO SUBMIT</AlertTitle>
          <div>
            Submitting this form won't call the FedEx API, and a truck WILL NOT
            show up.
          </div>
        </Alert>
      )}
      <NextStepButton text="Schedule pickup" />
      <LoadingOverlay
        isOpen={fetchState.isLoading || fetchState.data}
        title="Scheduling pickup"
      />
    </>
  );
};

const filterPickupWindows = (
  windows: ProposedFedExPickupTime[],
  cutOff: number
): ProposedFedExPickupTime[] => {
  const now = new Date();
  return windows.filter(window => {
    const date = parseDate(
      window.date + " " + window.startTime,
      "yyyy-MM-dd HH:mm:ss",
      new Date()
    );
    return differenceInHours(date, now) < cutOff;
  });
};
